/**
 * The shared look of the item cards (ExplorationCard, SelectionCard,
 * ReproductionCard): an outlined card with a dark header band holding the
 * name and the actions, fact rows below, buttons at the bottom.
 *
 * Kept as plain sx objects so the three cards and the Projects page render
 * the same thing from one place.
 */

import { ITEM_KIND_STYLES } from "./itemKindStyles";

export const MONO_FONT = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

export const ENTITY_CARD_SX = {
    borderRadius: 3,
    borderColor: "rgba(15, 23, 42, 0.08)",
    bgcolor: "background.paper",
    transition: "box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease",
    minWidth: 0,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    // clip the dark header band to the rounded corners
    overflow: "hidden",
    "&:hover": {
        boxShadow: "0 10px 30px rgba(15, 23, 42, 0.08)",
        borderColor: "rgba(15, 23, 42, 0.14)",
        transform: "translateY(-2px)",
    },
};

/** A card picked for a multi-card drag (Ctrl-click on the Projects page). */
export const ENTITY_CARD_SELECTED_SX = {
    outline: "2px solid #4C7EFF",
    outlineOffset: "-2px",
    borderColor: "#4C7EFF",
};

export const CARD_HEADER_SX = {
    bgcolor: "#1b2a4a",
    px: 1.75,
    pt: 0.75,
    pb: 1,
};

/** The header band of a card of the given kind (see ITEM_KIND_STYLES): the
 *  same navy for every kind, the kind shown by a slim stripe along the top.
 *  A grid of fifty cards stays calm that way. */
export const cardHeaderSx = (kind) => ({
    ...CARD_HEADER_SX,
    borderTop: `4px solid ${ITEM_KIND_STYLES[kind]?.accent || CARD_HEADER_SX.bgcolor}`,
});

export const CARD_NAME_SX = {
    fontFamily: MONO_FONT,
    fontWeight: 700,
    fontSize: "0.82rem",
    color: "#ffffff",
    lineHeight: 1.35,
    wordBreak: "break-word",
    mt: 0.15,
};

/** The small icon buttons in the header band (download, move, delete). */
export const HEADER_ICON_SX = {
    color: "rgba(255,255,255,0.6)",
    "&:hover": { color: "#ffffff" },
    "&.Mui-disabled": { color: "rgba(255,255,255,0.25)" },
};

export const HEADER_DELETE_SX = {
    color: "rgba(255,255,255,0.6)",
    "&:hover": { color: "#ff8a80" },
    "&.Mui-disabled": { color: "rgba(255,255,255,0.25)" },
};

export const CARD_CONTENT_SX = {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    p: 1.75,
    pt: 1.25,
    "&:last-child": { pb: 1.5 },
};

/** The filled action of a card, in the navy of its header band. */
export const PRIMARY_BUTTON_SX = {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 2,
    flex: 1,
    minWidth: 0,
    bgcolor: "#1b2a4a",
    "&:hover": { bgcolor: "#27395f" },
};

export const SECONDARY_BUTTON_SX = {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 2,
    borderColor: "rgba(15, 23, 42, 0.16)",
    color: "text.primary",
    flexShrink: 0,
    "&:hover": {
        borderColor: "rgba(15, 23, 42, 0.3)",
        bgcolor: "rgba(15, 23, 42, 0.03)",
    },
};

/** The status dot + label at the top of the header band. */
export const STATUS_LABEL_SX = { fontWeight: 600, fontSize: "0.78rem", color: "rgba(255,255,255,0.92)" };

/** The responsive card grid: one, two or three columns by width. */
export const CARD_GRID_SX = {
    display: "grid",
    gap: 2,
    width: "100%",
    gridTemplateColumns: {
        xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(3, minmax(0, 1fr))",
    },
};

/** The action row at the bottom of a card. */
export const CARD_ACTIONS_SX = { mt: "auto", pt: 1.25 };
